import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import type {
  StringIndexedObject,
  TableHeaderColumn,
  TableProps,
} from '@kanda-libs/ks-component-ts';

import { URLS } from 'common/constants';
import {
  DEFAULT_COLUMN,
  SEARCH_OPTIONS,
  TABLE_COLUMNS,
} from 'components/company/CompanyTable/constants';
import {
  FormattedCompany,
  sortData,
} from 'components/company/CompanyTable/helpers';
import useCompanyTableSorting from 'components/company/CompanyTable/useCompanyTableSorting';
import usePagination from 'hooks/usePagination';
import useSearchQuery from 'hooks/useSearchQuery';
import { selectors } from 'store';
import companySelectors from 'store/selectors/company';

export default function UseCompanyTableProps(): TableProps {
  const { push } = useHistory();

  const isLoading = useSelector(selectors.getIsLoading);
  const formattedCompanies = useSelector(
    companySelectors.getFormattedCompanies,
  );

  const { hits } = useSearchQuery(
    formattedCompanies,
    'companies',
    SEARCH_OPTIONS,
  );

  // TODO
  const completedSetup = false;

  const onRowClicked = useCallback((row?: StringIndexedObject) => {
    if (row) {
      push(URLS.COMPANY.replace('{id}', row.original?.id));
    }
  }, []);

  const { sorting, order, onAction } = useCompanyTableSorting();

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits?.length || 0,
  );

  const data: FormattedCompany[] | null = useMemo(() => {
    const rawData = hits.map((hit) => hit.item);
    return (
      sortData(rawData || [], sorting, order)?.slice(
        pageIndex * perPage,
        pageIndex * perPage + perPage,
      ) || []
    );
  }, [hits, sorting, order, pageIndex, perPage]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex],
  );

  useEffect(() => {
    setPage(0);
  }, [completedSetup, setPage]);

  return {
    data: data || [],
    columns: TABLE_COLUMNS as unknown as TableHeaderColumn[],
    defaultColumn: DEFAULT_COLUMN as unknown as TableHeaderColumn,
    hoverPopover: null,
    onRowClicked,
    onAction,
    pageIndex,
    totalPages,
    setPage,
    isLoading,
    isValidating: false,
  };
}
